import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAgreementListApi,
  useClientApi,
  useInvoiceApi,
} from '@hero/api-hooks';
import {
  AgreementEditDrawer,
  ClientLegalDrawer,
  UploadInvoiceProofDrawer,
} from '@hero/web-components';
import type { Client, Id, Invoice, InvoiceBeneficiary } from '@hero/schema';
import {
  RequirementsCard,
  RequirementsCardSkeleton,
  clientLegalHardChecks,
  clientLegalSoftChecks,
  isAgreementComplete,
  isClientLegalHardComplete,
  isClientLegalSoftComplete,
  pickCurrentAgreement,
} from '../Requirements';
import { HeroInvoiceProofCard } from './HeroInvoiceProofCard';

export interface HeroInvoiceRequirementsListProps {
  /**
   * `Invoice._id` of the picked Hero invoice. The list lazy-fetches
   * the full invoice + buyer client + bilateral agreement so the
   * parent fieldset only has to carry the picked id around.
   *
   * Pass an empty string / `undefined` when nothing is picked yet —
   * the list renders nothing in that case (the selector itself is
   * the only thing the user sees pre-pick).
   */
  invoiceId: string | undefined;
}

/**
 * `HeroInvoiceRequirementsList` — Lender-packet gate stack for the
 * financing apply wizard's "Use a Hero invoice" tab.
 *
 * Mirrors the external tab's gate stack (contract + customer legal
 * cards from `RequirementsCard`) but layers the Hero-specific
 * "Invoice details" card on top (VAT PDF / number / expected due
 * date). All
 * three cards open their own editor drawer; saving any of them
 * refetches automatically via the api-hook layer's mutation
 * invalidation, so the gate states update without the parent
 * having to re-mount.
 *
 * The component owns all the data fetching internally and
 * intentionally exposes *no* validation callbacks — completeness
 * is read off the same predicates (`isHeroInvoiceProofComplete`,
 * `isAgreementComplete`, `isClientLegalHardComplete`) that the
 * wizard's Continue button uses, so the gate UI and the submit
 * gate share a single source of truth.
 */
export function HeroInvoiceRequirementsList({
  invoiceId,
}: HeroInvoiceRequirementsListProps) {
  const { t } = useTranslation('m-financing');

  /**
   * Lazy-fetch the picked invoice. `useInvoiceApi(undefined)` is
   * a no-op stub when the id is missing, so we can mount this
   * component unconditionally and avoid a render thrash when the
   * user picks/unpicks.
   */
  const invoiceQuery = useInvoiceApi(
    invoiceId ? (invoiceId as unknown as Id) : undefined,
  );
  const invoice: Invoice | undefined = invoiceQuery.data;

  /**
   * Buyer-as-Client resolution. The invoice beneficiary's
   * `target` field still uses the legacy `business_customer` /
   * `individual_customer` enum (see
   * `apps/backend/src/app/payment/invoice/invoice.beneficiary.service.ts
   * → partyTargetToBeneficiaryTarget`), so we can't gate on
   * `target === 'client'`. Instead we read the CRM `Client._id`
   * directly off `buyer.ids[]` — the contract leg's tenant
   * builder always emits a `client_id` entry when the buyer
   * is sourced from CRM, which is the dominant case.
   *
   * When no `client_id` is present (buyer is an Org, Driver, or
   * raw User), the client/agreement gates are skipped — those
   * counterparties don't have a CRM Client doc to gate against.
   */
  const buyerClientId = extractClientId(invoice?.buyer);

  const clientQuery = useClientApi(buyerClientId);
  const pickedClient: Client | undefined = clientQuery.data;

  const agreementListQuery = useAgreementListApi(
    { partyId: buyerClientId },
    { enabled: !!buyerClientId },
  );
  const agreementsForClient = useMemo(
    () => agreementListQuery.data ?? [],
    [agreementListQuery.data],
  );
  const currentAgreement = useMemo(
    () => pickCurrentAgreement(agreementsForClient),
    [agreementsForClient],
  );

  /**
   * The contract / customer-legal cards need both the client doc
   * AND the agreement list before they can truthfully report
   * status. While either query is in flight, swap the gate cards
   * for neutral skeletons so the user doesn't see red "Required"
   * badges that flip green a moment later. Only check
   * `pickedClient?._id` once the query has actually settled —
   * otherwise a 404 / mismatched seed id would leave the cards
   * stuck on the skeleton forever.
   */
  const gatesLoading =
    !!buyerClientId &&
    (clientQuery.isLoading || agreementListQuery.isLoading);

  // Drawer open state — each gate owns its own dialog. They share
  // the same on-close handler shape (`setOpen(false)`) so we just
  // hold three booleans instead of one tagged enum.
  const [isProofDrawerOpen, setIsProofDrawerOpen] = useState(false);
  const [isAgreementDrawerOpen, setIsAgreementDrawerOpen] = useState(false);
  const [isClientLegalDrawerOpen, setIsClientLegalDrawerOpen] = useState(false);

  if (!invoiceId) return null;

  // Surface the exact missing client-legal fields inside the gate
  // card so the user knows what to fix without opening the drawer.
  // Hard fields (CR/Tax id/name) block the application; soft
  // fields (CR/Tax document scans) only soften the offer terms.
  const clientHardChecks = clientLegalHardChecks(pickedClient);
  const clientSoftChecks = clientLegalSoftChecks(pickedClient);
  const clientHardMissing = [
    !clientHardChecks.registrationName &&
      t('apply_form.invoice.external.customer_legal.missing.registration_name'),
    !clientHardChecks.registrationId &&
      t('apply_form.invoice.external.customer_legal.missing.registration_id'),
    !clientHardChecks.taxId &&
      t('apply_form.invoice.external.customer_legal.missing.tax_id'),
  ].filter(Boolean) as string[];
  const clientSoftMissing = [
    !clientSoftChecks.registrationImage &&
      t('apply_form.invoice.external.customer_legal.missing.registration_image'),
    !clientSoftChecks.taxImage &&
      t('apply_form.invoice.external.customer_legal.missing.tax_image'),
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-2.5">
      <HeroInvoiceProofCard
        invoice={invoice}
        onEdit={() => setIsProofDrawerOpen(true)}
      />

      {/*
       * Client-side gates — only meaningful when the buyer
       * resolved to a Client doc. When the buyer is an Org / User
       * we silently skip these (those flows already enforce
       * legal/contract upstream in our pilot scope, see
       * `computeHardBlocks` in `completeness.ts`).
       */}
      {buyerClientId ? (
        <div className="space-y-2" aria-busy={gatesLoading} aria-live="polite">
          {gatesLoading ? (
            <>
              <RequirementsCardSkeleton />
              <RequirementsCardSkeleton />
            </>
          ) : (
            <>
              <RequirementsCard
                title={t('apply_form.invoice.external.contract_card.title')}
                description={t(
                  currentAgreement
                    ? 'apply_form.invoice.external.contract_card.description_with_agreement'
                    : 'apply_form.invoice.external.contract_card.description_empty',
                  { client: pickedClient?.name ?? '' },
                )}
                complete={isAgreementComplete(currentAgreement)}
                level="hard"
                actionLabel={t(
                  currentAgreement
                    ? 'apply_form.invoice.external.contract_card.action_existing'
                    : 'apply_form.invoice.external.contract_card.action_create',
                )}
                onAction={() => setIsAgreementDrawerOpen(true)}
                missingItems={
                  isAgreementComplete(currentAgreement)
                    ? undefined
                    : currentAgreement
                      ? [t('apply_form.invoice.external.contract_card.missing_pdf')]
                      : [t('apply_form.invoice.external.contract_card.missing_agreement')]
                }
              />

              <RequirementsCard
                title={t('apply_form.invoice.external.customer_legal.title')}
                description={t(
                  'apply_form.invoice.external.customer_legal.description',
                )}
                complete={
                  isClientLegalHardComplete(pickedClient) &&
                  isClientLegalSoftComplete(pickedClient)
                }
                level={!isClientLegalHardComplete(pickedClient) ? 'hard' : 'soft'}
                actionLabel={t('apply_form.invoice.external.customer_legal.action')}
                onAction={() => setIsClientLegalDrawerOpen(true)}
                missingItems={
                  isClientLegalHardComplete(pickedClient) &&
                  isClientLegalSoftComplete(pickedClient)
                    ? undefined
                    : [...clientHardMissing, ...clientSoftMissing]
                }
              />
            </>
          )}
        </div>
      ) : null}

      {/*
       * Editor drawers — mounted unconditionally so their close
       * animations can play. Visibility is fully driven by the
       * `isOpen` props. The proof drawer reuses the same shared
       * component as the CRM "upload VAT invoice" flow; saving it
       * triggers an `invoice` entity invalidation in the api-hook
       * layer which re-renders the proof card automatically.
       */}
      <UploadInvoiceProofDrawer
        invoice={invoice ?? null}
        open={isProofDrawerOpen}
        onOpenChange={setIsProofDrawerOpen}
      />

      <AgreementEditDrawer
        isOpen={isAgreementDrawerOpen}
        onClose={() => setIsAgreementDrawerOpen(false)}
        agreementId={currentAgreement?._id}
        defaults={
          buyerClientId
            ? {
                counterpartyId: buyerClientId,
                counterpartyName: pickedClient?.name ?? invoice?.buyer?.name ?? '',
                counterpartyRoles: pickedClient?.roles,
              }
            : undefined
        }
        lockedFields={['counterparty']}
        displayFor="financing"
      />

      <ClientLegalDrawer
        isOpen={isClientLegalDrawerOpen}
        onClose={() => setIsClientLegalDrawerOpen(false)}
        client={pickedClient}
        displayFor="financing"
      />
    </div>
  );
}

/**
 * Pull the CRM `Client._id` out of an invoice beneficiary, when
 * the beneficiary is sourced from a Client doc. The contract
 * leg's tenant builder always emits a `client_id` entry on
 * `Party.ids[]` for CRM buyers (see
 * `apps/backend/src/app/order/tenants.builder.ts`), even when the
 * beneficiary's coarser `target` field collapses to
 * `business_customer` (the legacy enum).
 *
 * Returns `undefined` for non-CRM buyers (Org / Driver / raw
 * User) — there's no `Client` doc to gate against in those cases.
 */
function extractClientId(
  buyer: InvoiceBeneficiary | undefined,
): string | undefined {
  if (!buyer) return undefined;
  const fromIds = buyer.ids?.find(e => e.type === 'client_id')?.id;
  if (fromIds) return fromIds;
  // Modern bilateral writes that DO use the new `target: 'client'`
  // value still need to resolve, so we keep this as a secondary
  // fallback even though it's currently unused on the legacy
  // capture path.
  if (buyer.target === 'client') return String(buyer._id);
  return undefined;
}
